import * as benchlingUtil from 'BenchlingUtil';
import * as EventConstants from 'constants/EventConstants';
import {FolderSection} from 'constants/FolderConstants';
import OnboardingDAO from 'dao/OnboardingDAO';
import * as UserDAO from 'dao/UserDAO';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import FolderDAOCache from 'file-browser/FolderDAOCache';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import AppDispatcher from 'lib/Dispatcher';
import navigateToURL from 'lib/navigateToURL';
import * as FullScreenActions from 'nav/full-screen/FullScreenActions';
import listFolderSection from 'nav/sidepanel/listFolderSection';
import notifications from 'notifications';

import {
  JOIN_ORGANIZATION_SCREEN,
  NewUserFlow,
  NewUserFlowScreen,
  TERMINAL_SCREENS,
} from './NewUserFlowConstants';

function sendIsLoading(): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.NEW_USER_FLOW_IS_LOADING,
  });
}

function sendDoneLoading(): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.NEW_USER_FLOW_DONE_LOADING,
  });
}

export function showScreen(screen: NewUserFlowScreen): void {
  // If a next param is included in the URL, skip the final screen in the Getting Started flow and redirect
  const {queryKey} = benchlingUtil.parseUri(window.location.href);
  if (queryKey.next && TERMINAL_SCREENS.includes(screen)) {
    FullScreenActions.hideScreen();
    navigateToURL(decodeURIComponent(queryKey.next));
    return;
  }

  FullScreenActions.showScreen(screen);
}

export function hideScreen(): void {
  FullScreenActions.hideScreen();
}

export function beginFlow(flow: NewUserFlow): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.BEGIN_NEW_USER_FLOW,
    flow,
  });
}

export function showNextScreen(): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.SHOW_NEXT_NEW_USER_FLOW_SCREEN,
  });
}

export function nameCustomEntry({entryName, projectId}: {entryName: string; projectId?: string}): void {
  sendIsLoading();

  OnboardingDAO.nameCustomEntry({entryName, projectId})
    .fail(notifications.notifyError)
    .always(() => {
      sendDoneLoading();
    })
    .done((response) => {
      FileBrowserActions.openFile(response!.id);
      listFolderSection(response!.folder, FolderSection.NOTEBOOK);
      showNextScreen();
    });
}

export function nameCustomProject({projectName}: {projectName: string}): void {
  sendIsLoading();

  OnboardingDAO.nameCustomProject({projectName})
    .fail(notifications.notifyError)
    .always(() => {
      sendDoneLoading();
    })
    .done((response) => {
      const {api_identifier: folderId} = response!.rootFolder;

      AppDispatcher.dispatch({
        actionType: EventConstants.UPDATE_NEW_USER_FLOW_PROJECT,
        projectId: response!.id,
      });

      FileBrowserActions.fetchSources();
      FolderDAOCache.invalidateFetchAll();

      FolderDefaults.set(FolderDefaultsKey.NOTEBOOK, folderId);
      FolderDefaults.set(FolderDefaultsKey.INVENTORY, folderId);

      showNextScreen();
    });
}

export function submitRoleDetails(
  {
    phoneNumber,
    title,
    teamName,
    company,
  }: {phoneNumber: string | null; title: string | null; teamName: string | null; company: string | null},
  inGettingStartedFlow: boolean
): void {
  sendIsLoading();

  UserDAO.updateMetadata({phoneNumber, title, teamName, company})
    .fail(notifications.notifyError)
    .always(() => {
      sendDoneLoading();
    })
    .done(() => {
      if (inGettingStartedFlow) {
        showScreen(JOIN_ORGANIZATION_SCREEN);
      } else {
        navigateToURL('/editor');
      }
    });
}

export function submitIndustryRoleInfo(
  {
    company,
    title,
    phoneNumber,
    industry,
  }: {company: string | null; title: string | null; phoneNumber: string | null; industry: string | null},
  onFinish: () => void
): void {
  sendIsLoading();

  UserDAO.updateMetadata({company, title, phoneNumber, industry})
    .fail(notifications.notifyError)
    .always(() => {
      sendDoneLoading();
    })
    .done(() => {
      onFinish();
    });
}

export function submitAcademicRoleInfo(
  {institutionId, role, field}: {institutionId: string | null; role: string | null; field: string | null},
  onFinish: () => void
): void {
  sendIsLoading();

  UserDAO.updateMetadata({institutionId, academicRole: role, scientificField: field})
    .fail(notifications.notifyError)
    .always(() => {
      sendDoneLoading();
    })
    .done(() => {
      onFinish();
    });
}
